// Shared sync wiring for the web app: build the right SyncTarget from config +
// session, run a sync, and a background auto-sync hook. Local-first by default
// — when no cloud target is configured this is all a graceful no-op.
import { useEffect, useRef } from 'react';
import {
  RestSyncTarget,
  SupabaseSyncTarget,
  SyncEngine,
  systemClock,
  type SyncTarget,
  type SyncOutcome,
  type LocalStore,
} from '@life-os/data';
import { appConfig } from './config.js';
import { cloudApiUrl, getToken } from './cloud.js';

/**
 * The real cloud target for this session, or null when none is configured.
 * Prefers OUR own API (RestSyncTarget + JWT); falls back to a direct Supabase
 * target if an anon key is present. Returns null for the loopback-only case so
 * background sync doesn't spin uselessly.
 */
export function buildSyncTarget(): SyncTarget | null {
  const apiUrl = cloudApiUrl();
  const token = getToken();
  if (apiUrl && token) return new RestSyncTarget({ baseUrl: apiUrl, token });
  if (appConfig.supabaseUrl && appConfig.supabaseAnonKey) {
    return new SupabaseSyncTarget({ url: appConfig.supabaseUrl, apiKey: appConfig.supabaseAnonKey });
  }
  return null;
}

/** Run one sync against the configured target, or return null if none. */
export async function runSync(store: LocalStore): Promise<SyncOutcome | null> {
  const target = buildSyncTarget();
  if (!target) return null;
  return new SyncEngine(store, target, systemClock).sync();
}

const AUTO_SYNC_INTERVAL_MS = 45_000;
const POST_EDIT_DEBOUNCE_MS = 400;
const REALTIME_DEBOUNCE_MS = 200;

/**
 * Background sync: keeps every device current without a manual button.
 * Triggers on mount, on reconnect, on a periodic interval, and (debounced)
 * shortly after a local edit so changes push out promptly. Runs are
 * non-overlapping and skipped while offline. When a sync pulls remote changes
 * or surfaces conflicts, it calls `onChanged` so screens re-query.
 */
export function useAutoSync(store: LocalStore, revision: number, onChanged: () => void): void {
  const running = useRef(false);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const tick = useRef(async () => {
    if (running.current) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (!buildSyncTarget()) return;
    running.current = true;
    try {
      const outcome = await runSync(store);
      if (outcome && (outcome.pulled > 0 || outcome.conflicted > 0)) onChangedRef.current();
    } catch {
      // Transient/offline — Runtime Status reflects health; never throw here.
    } finally {
      running.current = false;
    }
  });

  // Mount + reconnect + interval.
  useEffect(() => {
    const fire = () => void tick.current();
    fire();
    const onOnline = () => fire();
    window.addEventListener('online', onOnline);
    const timer = window.setInterval(fire, AUTO_SYNC_INTERVAL_MS);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(timer);
    };
  }, [store]);

  // Debounced push shortly after a local change (revision bump).
  useEffect(() => {
    if (revision === 0) return;
    const id = window.setTimeout(() => void tick.current(), POST_EDIT_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [revision]);

  // Real-time pull: subscribe to the server's change stream (SSE) so a write on
  // any other device lands here within ~200ms — Notion-style instant sync. The
  // EventSource auto-reconnects; this is a no-op without an API target.
  useEffect(() => {
    const apiUrl = cloudApiUrl();
    const token = getToken();
    if (!apiUrl || !token) return;
    let stopped = false;
    let debounce: number | undefined;
    const es = new EventSource(`${apiUrl}/sync/events?token=${encodeURIComponent(token)}`);
    es.addEventListener('change', () => {
      if (stopped) return;
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void tick.current(), REALTIME_DEBOUNCE_MS);
    });
    return () => {
      stopped = true;
      window.clearTimeout(debounce);
      es.close();
    };
  }, [store]);
}
